import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import IconButton from "components/IconButton";
import CellModelOrRecipeParameterFields from "./components/CellModelOrRecipeParameterFields";
import AlternativeStepFields from "./components/AlternativeStepFields";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import ResultElementParameterSelect from "../../../ResultElementParameterSelect";
import { ParameterFields } from "../../types";
import { parameterTypesSelector } from "store/global/global.selectors";
import { RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { serverErrorsSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { getErrorByPropertyName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { ResultElementParameterType, } from "store/rs-workspace/rs-workspace.types";
import { workspaceNotRefinedCellModelsSelector, workspaceRecipesSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getCombinationParameterName } from "store/rs-workspace/utils/common.utils";
import DeleteIcon from "icons/delete.svg";
import * as styles from "../../styles.module.scss";
const ParameterRow = ({ parameter, name, parameters, sources, restrictions = {}, upsert, remove, }) => {
    const [parameterDialogOpen, setParameterDialogOpen] = useState(false);
    const parameterTypes = useSelector(parameterTypesSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const cellModels = useSelector(workspaceNotRefinedCellModelsSelector);
    const serverErrors = useSelector(serverErrorsSelector(RequestTemplate.SPACEVIEWER_ELEMENT_PUT));
    const parameterName = useMemo(() => getCombinationParameterName(parameter, recipes, cellModels, parameterTypes), [cellModels, parameter, parameterTypes, recipes]);
    const deleteRow = useCallback(() => remove(parameter?.id), [remove, parameter?.id]);
    const onSubmitParameterSource = useCallback((values) => {
        const parameterValue = {
            id: parameter?.id,
            parameter: values,
        };
        if (values.type === ResultElementParameterType.CELL_MODEL ||
            values.type === ResultElementParameterType.RECIPE) {
            parameterValue.parameter = {
                ...values,
                [ParameterFields.min]: null,
                [ParameterFields.max]: null,
                [ParameterFields.granularity]: null,
            };
        }
        else {
            parameterValue.parameter = {
                ...values,
                [ParameterFields.flowIndexes]: undefined,
            };
        }
        upsert(parameterValue);
        setParameterDialogOpen(false);
    }, [parameter?.id, upsert]);
    const selectedParameters = useMemo(() => [...parameters]
        .filter((selectedParameter) => selectedParameter.id !== parameter.id && selectedParameter.parameter)
        .map(({ parameter }) => parameter), [parameter.id, parameters]);
    return (React.createElement("div", { className: styles.row },
        React.createElement("div", { className: classNames(styles.cell, styles.parameter) },
            React.createElement(Input, { name: `${name}.${ParameterFields.parameter}.${ParameterFields.name}`, component: SelectInput, autoFocus: true, items: [
                    {
                        value: parameterName,
                        label: parameterName,
                    },
                ], value: parameterName, placeholder: "Select", itemRenderer: SelectItemRenderer, popoverProps: {
                    onInteraction: () => setParameterDialogOpen(true),
                }, classes: {
                    container: styles.selectWrapper,
                    component: styles.select,
                }, restrictions: { IsNecessary: true }, externalError: getErrorByPropertyName(`Request.${name}.parameter.parameterName`, serverErrors)?.text }),
            parameterDialogOpen && (React.createElement(ResultElementParameterSelect, { title: parameter?.parameter ? "Edit Parameter" : "New parameter", initialValues: (parameter?.parameter ?? undefined), noRefinedCellModels: true, allowMultipleUnits: false, allowedTypes: [ResultElementParameterType.RECIPE, ResultElementParameterType.CELL_MODEL], selectedParameters: selectedParameters, onSubmit: onSubmitParameterSource, onClose: () => setParameterDialogOpen(false), restrictions: restrictions?.Parameter, classes: { target: styles.parameterDialogTarget } }))),
        parameter.parameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW ? (React.createElement(AlternativeStepFields, { name: name, parameter: parameter, sources: sources, restrictions: restrictions?.Parameter?.AlternativeFlow })) : (React.createElement(CellModelOrRecipeParameterFields, { name: name, parameter: parameter, sources: sources, restrictions: parameter.parameter?.type === ResultElementParameterType.CELL_MODEL
                ? restrictions?.Parameter?.CellModel
                : restrictions?.Parameter?.Recipe })),
        React.createElement("div", { className: classNames(styles.cell, styles.delete) },
            React.createElement(IconButton, { icon: React.createElement(DeleteIcon, null), title: "Delete", onClick: deleteRow }))));
};
export default ParameterRow;
